import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { DEFAULT_LANGUAGE_CODES, LANGUAGE_META_BY_CODE } from '../i18n/languageMeta';
import './DriverWebApp.css';

const normalizeLang = (lang) => {
  if (!lang) return null;
  return String(lang).split('-')[0].toLowerCase();
};

const readQueryParams = (search) => {
  try {
    return new URLSearchParams(search || '');
  } catch {
    return new URLSearchParams();
  }
};

const getStoredToken = () => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('itraxx_token');
};

const setStoredToken = (token) => {
  if (typeof window === 'undefined') return;
  if (!token) {
    localStorage.removeItem('itraxx_token');
    return;
  }
  localStorage.setItem('itraxx_token', token);
};

const DISPATCH_LOGIN_URL = 'https://dispatch.itraxx.se';

async function validateToken(token) {
  const res = await fetch('/api/auth/validate-token', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) return null;
  const data = await res.json().catch(() => null);
  return data?.user || null;
}

async function fetchDriverProfile(token) {
  const res = await fetch('/api/driver/profile', {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return null;
  return res.json().catch(() => null);
}

async function fetchDriverFairnessStatus(token) {
  const res = await fetch('/api/driver/fairness-status', {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return null;
  return res.json().catch(() => null);
}

async function postDriverStatus(token, status) {
  await fetch('/api/driver/status', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ status }),
  });
}

const Toast = ({ message }) => {
  if (!message) return null;
  return <div className="driver-web__toast">{message}</div>;
};

const TabButton = ({ active, icon, label, onClick }) => {
  return (
    <button
      type="button"
      className={`driver-web__tab ${active ? 'driver-web__tab--active' : ''}`}
      onClick={onClick}
    >
      <div className="driver-web__tabIcon">{icon}</div>
      <div className="driver-web__tabLabel">{label}</div>
    </button>
  );
};

function DriverWebApp() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const [bootState, setBootState] = useState({ loading: true, authed: false });
  const [driverUser, setDriverUser] = useState(null);
  const [driverProfile, setDriverProfile] = useState(null);
  const [driverFairness, setDriverFairness] = useState(null);
  const [isOnline, setIsOnline] = useState(false);
  const [activeTab, setActiveTab] = useState('home');
  const [toastMessage, setToastMessage] = useState('');
  const toastTimerRef = useRef(null);

  const languages = useMemo(() => {
    return DEFAULT_LANGUAGE_CODES.map((code) => ({
      code,
      name: LANGUAGE_META_BY_CODE[code]?.name || code,
      flag: LANGUAGE_META_BY_CODE[code]?.flag || '',
    }));
  }, []);

  const showToast = (message) => {
    if (!message) return;
    setToastMessage(message);
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => setToastMessage(''), 1800);
  };

  useEffect(() => {
    const params = readQueryParams(location.search);
    const tokenParam = params.get('token') || params.get('auth') || params.get('t');
    if (tokenParam) setStoredToken(tokenParam);

    const langParam = normalizeLang(
      params.get('lang') || params.get('language') || params.get('locale')
    );
    if (langParam) {
      localStorage.setItem('itraxx_driver_web_lang', langParam);
      i18n.changeLanguage(langParam).catch(() => {});
    } else {
      const storedLang = normalizeLang(localStorage.getItem('itraxx_driver_web_lang'));
      if (storedLang) i18n.changeLanguage(storedLang).catch(() => {});
    }
  }, [i18n, location.search]);

  useEffect(() => {
    let cancelled = false;

    const boot = async () => {
      setBootState({ loading: true, authed: false });

      const token = getStoredToken();
      if (!token) {
        if (!cancelled) setBootState({ loading: false, authed: false });
        return;
      }

      try {
        const user = await validateToken(token);
        if (!user) {
          setStoredToken(null);
          if (!cancelled) setBootState({ loading: false, authed: false });
          return;
        }

        const [profile, fairnessStatus] = await Promise.all([
          fetchDriverProfile(token),
          fetchDriverFairnessStatus(token),
        ]);
        if (!cancelled) {
          setDriverUser(user);
          setDriverProfile(profile);
          setDriverFairness(fairnessStatus);
          setBootState({ loading: false, authed: true });
        }
      } catch {
        if (!cancelled) setBootState({ loading: false, authed: false });
      }
    };

    boot();
    return () => {
      cancelled = true;
    };
  }, [location.key]);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    };
  }, []);

  const onLogout = () => {
    const ok = window.confirm(t('driver_web.confirm_logout'));
    if (!ok) return;
    setStoredToken(null);
    setDriverUser(null);
    setDriverProfile(null);
    setDriverFairness(null);
    setBootState({ loading: false, authed: false });
    navigate(location.pathname || '/driver', { replace: true });
  };

  const onToggleStatus = async () => {
    const token = getStoredToken();
    if (!token) return;

    const nextOnline = !isOnline;
    setIsOnline(nextOnline);
    showToast(nextOnline ? t('driver_web.status.toast_online') : t('driver_web.status.toast_offline'));

    try {
      await postDriverStatus(token, nextOnline ? 'online' : 'offline');
    } catch {
    }
  };

  const currentLangCode = normalizeLang(i18n.language) || 'sv';
  const fairness = driverFairness?.fairness || null;
  const queueStatus = driverFairness?.zone_queue || null;
  const fairnessMultiplierPercent = fairness
    ? `${Math.round(Number(fairness.multiplier || 1) * 100)}%`
    : '—';
  const fairnessTripTarget = fairness
    ? `${Number(fairness.trips_today || 0)} / ${Number(fairness.max_trips_before_penalty || 0)}`
    : '—';

  const demoAssignment = useMemo(() => {
    return {
      id: 'demo-1',
      title: t('driver_web.jobs.new_assignment'),
      pickup: 'Storgatan 12',
      destination: 'Centralstationen',
      state: 'new',
    };
  }, [t]);

  const [demoJob, setDemoJob] = useState(null);

  useEffect(() => {
    if (!bootState.authed) {
      setDemoJob(null);
      return;
    }
    if (demoJob) return;
    const timer = window.setTimeout(() => setDemoJob(demoAssignment), 700);
    return () => window.clearTimeout(timer);
  }, [bootState.authed, demoAssignment, demoJob]);

  const updateDemoJobState = (nextState) => {
    if (!demoJob) return;
    setDemoJob({ ...demoJob, state: nextState });
  };

  const renderJobsActions = () => {
    if (!demoJob) return null;

    if (demoJob.state === 'new') {
      return (
        <div className="driver-web__row">
          <button
            type="button"
            className="driver-web__button driver-web__button--primary"
            onClick={() => {
              updateDemoJobState('accepted');
              showToast(t('driver_web.toast.accepted'));
              setActiveTab('home');
            }}
          >
            {t('driver_web.jobs.accept')}
          </button>
          <button
            type="button"
            className="driver-web__button driver-web__button--danger"
            onClick={() => {
              updateDemoJobState('rejected');
              showToast(t('driver_web.toast.rejected'));
              window.setTimeout(() => setDemoJob(null), 250);
            }}
          >
            {t('driver_web.jobs.reject')}
          </button>
        </div>
      );
    }

    if (demoJob.state === 'accepted') {
      return (
        <button
          type="button"
          className="driver-web__button driver-web__button--primary"
          onClick={() => {
            updateDemoJobState('on_the_way');
            showToast(t('driver_web.jobs.on_the_way'));
            setActiveTab('home');
          }}
        >
          {t('driver_web.jobs.on_the_way')}
        </button>
      );
    }

    if (demoJob.state === 'on_the_way') {
      return (
        <button
          type="button"
          className="driver-web__button driver-web__button--primary"
          onClick={() => {
            updateDemoJobState('arrived');
            showToast(t('driver_web.toast.arrived_pickup'));
          }}
        >
          {t('driver_web.jobs.arrived_pickup')}
        </button>
      );
    }

    if (demoJob.state === 'arrived') {
      return (
        <button
          type="button"
          className="driver-web__button driver-web__button--primary"
          onClick={() => {
            updateDemoJobState('in_trip');
            showToast(t('driver_web.toast.trip_started'));
          }}
        >
          {t('driver_web.jobs.start_trip')}
        </button>
      );
    }

    if (demoJob.state === 'in_trip') {
      return (
        <button
          type="button"
          className="driver-web__button driver-web__button--primary"
          onClick={() => {
            updateDemoJobState('completed');
            showToast(t('driver_web.toast.trip_completed'));
          }}
        >
          {t('driver_web.jobs.complete_trip')}
        </button>
      );
    }

    return null;
  };

  if (bootState.loading) {
    return (
      <div className="driver-web driver-web--loading">
        <div className="driver-web__spinner" />
        <div className="driver-web__loadingText">{t('driver_web.loading')}</div>
      </div>
    );
  }

  if (!bootState.authed) {
    return (
      <div className="driver-web driver-web--login">
        <div className="driver-web__loginCard">
          <div className="driver-web__loginTitle">{t('driver_web.login.title')}</div>
          <div className="driver-web__loginBody">{t('driver_web.login.body')}</div>
          <a className="driver-web__loginButton" href={DISPATCH_LOGIN_URL}>
            {t('driver_web.login.button')}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="driver-web">
      <div className="driver-web__container">
        <div className="driver-web__header">
          <div className="driver-web__headerLeft">
            <div className="driver-web__title">{t('driver_web.header.title')}</div>
            <div className="driver-web__subtitle">
              {driverUser?.name ? driverUser.name : t('driver_web.profile.driver')}
              {driverUser?.email ? ` (${driverUser.email})` : ''}
            </div>
          </div>
          <div className="driver-web__headerRight">
            <label className="driver-web__langLabel">
              {t('driver_web.header.language')}
              <select
                className="driver-web__langSelect"
                value={currentLangCode}
                onChange={(e) => {
                  const nextLang = normalizeLang(e.target.value);
                  if (!nextLang) return;
                  localStorage.setItem('itraxx_driver_web_lang', nextLang);
                  i18n.changeLanguage(nextLang).catch(() => {});
                }}
              >
                {languages.map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.flag ? `${l.flag} ` : ''}
                    {l.name}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className="driver-web__logout" onClick={onLogout}>
              {t('driver_web.header.logout')}
            </button>
          </div>
        </div>

        <div className="driver-web__content">
          {activeTab === 'home' && (
            <div className="driver-web__view">
              <div className="driver-web__card">
                <div className="driver-web__cardTitle">{t('driver_web.status.title')}</div>
                <div className="driver-web__statusRow">
                  <span
                    className={`driver-web__statusBadge ${
                      isOnline ? 'driver-web__statusBadge--online' : 'driver-web__statusBadge--offline'
                    }`}
                  >
                    {isOnline ? t('driver_web.status.online') : t('driver_web.status.offline')}
                  </span>
                </div>
                <button
                  type="button"
                  className={`driver-web__button ${
                    isOnline ? 'driver-web__button--danger' : 'driver-web__button--primary'
                  }`}
                  onClick={onToggleStatus}
                >
                  {isOnline ? t('driver_web.status.set_offline') : t('driver_web.status.set_online')}
                </button>
              </div>

              <div className="driver-web__card">
                <div className="driver-web__cardTitle">{t('driver_web.jobs.title')}</div>
                {!demoJob ? (
                  <div className="driver-web__muted">{t('driver_web.jobs.none')}</div>
                ) : (
                  <div className="driver-web__job">
                    <div className="driver-web__jobTitle">{demoJob.title}</div>
                    <div className="driver-web__jobMeta">
                      <div>{t('driver_web.jobs.pickup')}: {demoJob.pickup}</div>
                      <div>{t('driver_web.jobs.to_destination')}: {demoJob.destination}</div>
                    </div>
                    <div className="driver-web__jobActions">{renderJobsActions()}</div>
                  </div>
                )}
              </div>

              <div className="driver-web__card">
                <div className="driver-web__cardTitle">Rättviseläge</div>
                {!fairness ? (
                  <div className="driver-web__muted">Rättviseläget laddas in när din förarsession är klar.</div>
                ) : (
                  <>
                    <div className="driver-web__statsGrid">
                      <div className="driver-web__stat">
                        <div className="driver-web__statLabel">Status</div>
                        <div className="driver-web__statValue">{fairness.state_label || 'Basläge'}</div>
                      </div>
                      <div className="driver-web__stat">
                        <div className="driver-web__statLabel">Vikt nu</div>
                        <div className="driver-web__statValue">{fairnessMultiplierPercent}</div>
                      </div>
                      <div className="driver-web__stat">
                        <div className="driver-web__statLabel">Resor idag</div>
                        <div className="driver-web__statValue">{fairnessTripTarget}</div>
                      </div>
                    </div>
                    <div className="driver-web__muted" style={{ marginTop: 12 }}>
                      {fairness.next_trip_message}
                    </div>
                    {queueStatus?.active ? (
                      <div className="driver-web__muted" style={{ marginTop: 10 }}>
                        Zonkö: plats {queueStatus.position} i {queueStatus.zone_name || 'aktiv zon'}.
                        Bonus i dispatch: +{queueStatus.bonus_score}.
                      </div>
                    ) : (
                      <div className="driver-web__muted" style={{ marginTop: 10 }}>
                        Ingen aktiv zonkö registrerad just nu.
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {activeTab === 'jobs' && (
            <div className="driver-web__view">
              <div className="driver-web__card">
                <div className="driver-web__cardTitle">{t('driver_web.jobs.title')}</div>
                {!demoJob ? (
                  <div className="driver-web__muted">{t('driver_web.jobs.none')}</div>
                ) : (
                  <div className="driver-web__job">
                    <div className="driver-web__jobTitle">{demoJob.title}</div>
                    <div className="driver-web__jobMeta">
                      <div>{t('driver_web.jobs.pickup')}: {demoJob.pickup}</div>
                      <div>{t('driver_web.jobs.to_destination')}: {demoJob.destination}</div>
                    </div>
                    <div className="driver-web__jobActions">{renderJobsActions()}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'earnings' && (
            <div className="driver-web__view">
              <div className="driver-web__card">
                <div className="driver-web__cardTitle">{t('driver_web.earnings.stats_title')}</div>
                <div className="driver-web__statsGrid">
                  <div className="driver-web__stat">
                    <div className="driver-web__statLabel">{t('driver_web.earnings.trips')}</div>
                    <div className="driver-web__statValue">{demoJob?.state === 'completed' ? '1' : '0'}</div>
                  </div>
                  <div className="driver-web__stat">
                    <div className="driver-web__statLabel">{t('driver_web.earnings.earned')}</div>
                    <div className="driver-web__statValue">—</div>
                  </div>
                  <div className="driver-web__stat">
                    <div className="driver-web__statLabel">{t('driver_web.earnings.online_time')}</div>
                    <div className="driver-web__statValue">{isOnline ? '—' : '0'}</div>
                  </div>
                </div>
                {driverProfile?.company_settings?.revenue_model && (
                  <div className="driver-web__muted" style={{ marginTop: 12 }}>
                    {String(driverProfile.company_settings.revenue_model)}
                  </div>
                )}
                {fairness && (
                  <div className="driver-web__muted" style={{ marginTop: 12 }}>
                    Rättviseläge: {fairness.state_label}. Vikt {fairnessMultiplierPercent} och {Number(fairness.trips_until_soft_penalty || 0)} resor kvar till nästa tröskel.
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'profile' && (
            <div className="driver-web__view">
              <div className="driver-web__card">
                <div className="driver-web__cardTitle">{t('driver_web.profile.title')}</div>
                <div className="driver-web__profileRow">
                  <div className="driver-web__profileLabel">{t('driver_web.profile.driver')}</div>
                  <div className="driver-web__profileValue">
                    {driverUser?.name ? driverUser.name : '—'}
                  </div>
                </div>
                <div className="driver-web__profileRow">
                  <div className="driver-web__profileLabel">{t('common.email')}</div>
                  <div className="driver-web__profileValue">
                    {driverUser?.email ? driverUser.email : '—'}
                  </div>
                </div>
                <button
                  type="button"
                  className="driver-web__button driver-web__button--primary"
                  onClick={() => {
                    setDemoJob(demoAssignment);
                    showToast(t('driver_web.toast.new_assignment'));
                    setActiveTab('jobs');
                  }}
                >
                  {t('driver_web.profile.show_demo_again')}
                </button>
                <button type="button" className="driver-web__button driver-web__button--danger" onClick={onLogout}>
                  {t('driver_web.profile.logout')}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="driver-web__tabs">
          <TabButton
            active={activeTab === 'home'}
            icon="🏠"
            label={t('driver_web.tabs.home')}
            onClick={() => setActiveTab('home')}
          />
          <TabButton
            active={activeTab === 'jobs'}
            icon="🧾"
            label={t('driver_web.tabs.jobs')}
            onClick={() => setActiveTab('jobs')}
          />
          <TabButton
            active={activeTab === 'earnings'}
            icon="💰"
            label={t('driver_web.tabs.earnings')}
            onClick={() => setActiveTab('earnings')}
          />
          <TabButton
            active={activeTab === 'profile'}
            icon="👤"
            label={t('driver_web.tabs.profile')}
            onClick={() => setActiveTab('profile')}
          />
        </div>
      </div>

      <Toast message={toastMessage} />
    </div>
  );
}

export default DriverWebApp;
